/**
 * Global Object ID Encoding System
 *
 * Provides utilities for encoding database object IDs into URL-safe global IDs
 * using two-character prefixes and base-36 encoding.
 *
 * Format: <prefix><encoded-id>
 * Example: "us1a" = User with ID 1
 *
 * @module globalObjectId
 */

// ============================================================================
// Type Registry
// ============================================================================

/**
 * Registry of all object types and their two-character prefixes.
 * This is the single source of truth for type/prefix mappings.
 */
export const OBJECT_TYPES = {
  users: 'us',
  photos: 'ph',
  photo_variants: 'pv',
  photo_versions: 'xv',  // Feature: 081-photo-versioning (using 'xv' since 'pv' is taken by photo_variants)
  collections: 'co',
  collection_items: 'ci',
  critiques: 'cr',
  critique_edits: 'ce',
  critique_sessions: 'cs',
  notifications: 'no',
  user_activities: 'ua',
  login_tokens: 'lt',  // Feature: 647-invite-registration
  tasks: 'ta',  // Feature: 078-user-tasks
  photo_ratings: 'pr',  // Feature: 649-critique-feedback-options
  engagement_graph_edges: 'eg',  // Feature: 080-critique-sharing
  share_events: 'se'  // Feature: 080-critique-sharing
} as const;

/**
 * Valid object type names (database table names)
 */
export type ObjectType = keyof typeof OBJECT_TYPES;

/**
 * Valid two-character prefixes
 */
export type ObjectPrefix = typeof OBJECT_TYPES[ObjectType];

/**
 * Reverse mapping from prefix to object type
 */
const PREFIX_TO_TYPE: Record<string, ObjectType> = Object.fromEntries(
  Object.entries(OBJECT_TYPES).map(([type, prefix]) => [prefix, type as ObjectType])
);

// ============================================================================
// Type Guards and Validation
// ============================================================================

/**
 * Type guard: Check if a string is a valid object type
 */
export function isValidObjectType(type: string): type is ObjectType {
  return type in OBJECT_TYPES;
}

/**
 * Type guard: Check if a string is a valid object prefix
 */
export function isValidPrefix(prefix: string): prefix is ObjectPrefix {
  return prefix in PREFIX_TO_TYPE;
}

/**
 * Validate global ID format (pattern matching only, doesn't check registry)
 *
 * Expected format: ^[a-z]{2}([0-9][a-z])+$
 * - 2 lowercase letters (prefix)
 * - 1+ pairs of [0-9][a-z] (encoded base-260 digits)
 * - Total length must be even (4, 6, 8, etc.)
 */
export function isValidGlobalIdFormat(globalId: string): boolean {
  if (typeof globalId !== 'string') return false;
  if (globalId.length < 4) return false; // Minimum: prefix (2) + one digit pair (2)
  if (globalId.length % 2 !== 0) return false; // Must be even length

  // Pattern: prefix ([a-z]{2}) + pairs of ([0-9][a-z])
  const pattern = /^[a-z]{2}([0-9][a-z])+$/;
  if (!pattern.test(globalId)) return false;

  // Check if prefix is registered
  const prefix = globalId.slice(0, 2);
  return isValidPrefix(prefix);
}

/**
 * Determine if a value is a global ID (vs numeric ID)
 *
 * Use this function in database query functions to distinguish between:
 * - Numeric IDs: number or numeric string (e.g., 123, "123")
 * - Global IDs: string with format ^[a-z]{2}([0-9][a-z])+$ (e.g., "co4m")
 *
 * Detection order:
 * 1. Numbers are always numeric IDs → false
 * 2. Numeric strings (e.g., "123") are numeric IDs → false
 * 3. Global ID format strings → validated via isValidGlobalIdFormat()
 * 4. Invalid formats → false (let caller handle error)
 *
 * @param value - Value to check (from API/database query)
 * @returns true if value is a global ID, false otherwise
 *
 * @example
 * isGlobalId(123)         // false (numeric ID)
 * isGlobalId("123")       // false (numeric string)
 * isGlobalId("co4m")      // true (global ID)
 * isGlobalId("invalid")   // false (invalid format)
 */
export function isGlobalId(value: string | number): boolean {
  // Rule 1: Numbers are always numeric IDs, not global IDs
  if (typeof value === 'number') return false;

  // Rule 2: Numeric strings are numeric IDs (e.g., "123", "0", "42")
  if (/^\d+$/.test(value)) return false;

  // Rule 3: Validate global ID format (prefix + base-260 encoding)
  return isValidGlobalIdFormat(value);
}

// ============================================================================
// Encoding Functions
// ============================================================================

/**
 * Ensure an ID is in global ID format with correct type
 *
 * This function asserts that the provided ID is already a valid global ID string
 * and validates that its type matches the expected type. This enforces the architectural
 * boundary that numeric IDs should never leak outside the API service tier.
 *
 * Input Requirements:
 * - Must be a string (not a number or bigint)
 * - Must be a valid global ID format (e.g., "co4m", "us1a")
 * - Must match the expected object type
 *
 * @param type - Expected object type (e.g., 'users', 'photos')
 * @param id - Global ID string to validate (e.g., "us1a", "co4m")
 * @returns The same global ID string (after validation)
 * @throws Error if type is invalid, id is not a global ID, or type mismatch detected
 *
 * @example
 * // Success case - valid global ID with correct type
 * assertGlobalId('collections', "co4m"); // Returns "co4m"
 *
 * @example
 * // Error cases - numeric IDs are no longer accepted
 * assertGlobalId('collections', 123);      // Throws: TypeScript compile error
 * assertGlobalId('collections', "123");    // Throws: Expected global ID but received numeric string
 *
 * @example
 * // Error case - type mismatch
 * assertGlobalId('users', "co4m");         // Throws: Type mismatch (expected users but got collections)
 */
export function assertGlobalId(type: ObjectType, id: string): string {
  if (!isValidObjectType(type)) {
    throw new Error(`Invalid object type: ${type}`);
  }

  // Assert that id is already a global ID (not a numeric ID)
  if (!isValidGlobalIdFormat(id)) {
    throw new Error(
      `Expected global ID for ${type}, but received: "${id}". ` +
      `Numeric IDs should not leak outside the API service boundary.`
    );
  }

  // Decode and validate type match
  const result = decodeGlobalId(id);
  if ('error' in result) {
    throw new Error(`Invalid global ID for ${type}: ${result.error}`);
  }

  if (result.type !== type) {
    throw new Error(
      `Type mismatch: expected ${type} but global ID "${id}" has type ${result.type}`
    );
  }

  return id;
}


/**
 * Encode a numeric ID using base-260
 *
 * Algorithm:
 * - Each base-260 digit (0-259) is encoded as two characters: [0-9][a-z]
 * - Encoding: digit / 26 gives number part (0-9), digit % 26 gives letter part (a-z)
 * - Process from right to left, building the result string
 *
 * Examples:
 * - 0 → "0a" (0%260=0 → 0÷26=0, 0%26=0)
 * - 1 → "0b" (1%260=1 → 1÷26=0, 1%26=1)
 * - 25 → "0z" (25%260=25 → 25÷26=0, 25%26=25)
 * - 26 → "1a" (26%260=26 → 26÷26=1, 26%26=0)
 * - 259 → "9z" (259%260=259 → 259÷26=9, 259%26=25)
 * - 260 → "0a0a" (260÷260=1, 260%260=0 → "0a" + "0a")
 *
 * @param id - Numeric ID (number or BigInt)
 * @returns Encoded string (always even length, ends with letter)
 */
export function encodeId(id: number | bigint): string {
  if (id < 0) {
    throw new Error('ID must be non-negative');
  }

  // Convert to BigInt for consistent handling
  let n = typeof id === 'bigint' ? id : BigInt(id);

  // Check max 64-bit integer
  const MAX_INT64 = BigInt('9223372036854775807');
  if (n > MAX_INT64) {
    throw new Error('ID exceeds 64-bit integer maximum');
  }

  // Special case: 0
  if (n === BigInt(0)) {
    return '0a';
  }

  let result = '';
  const CHAR_0 = 48; // '0'.charCodeAt(0)
  const CHAR_A = 97; // 'a'.charCodeAt(0)

  while (n > BigInt(0)) {
    // Get the current base-260 digit (0-259)
    const part = Number(n % BigInt(260));

    // Encode this digit as [0-9][a-z]
    const letterPart = part % 26;
    const numberPart = (part - letterPart) / 26;

    const pair = String.fromCharCode(numberPart + CHAR_0) +
                 String.fromCharCode(letterPart + CHAR_A);

    // Prepend to result (building right to left)
    result = pair + result;

    // Move to next base-260 digit
    n = (n - BigInt(part)) / BigInt(260);
  }

  return result;
}

/**
 * Decode a base-260 string back to a numeric ID
 *
 * Algorithm:
 * - Process string in pairs of two characters [0-9][a-z]
 * - Each pair represents a base-260 digit (0-259)
 * - Decoding: number_part × 26 + letter_part
 * - Combine digits using base-260 positional notation
 *
 * Examples:
 * - "0a" → 0×26+0 = 0
 * - "0b" → 0×26+1 = 1
 * - "0z" → 0×26+25 = 25
 * - "1a" → 1×26+0 = 26
 * - "9z" → 9×26+25 = 259
 * - "0a0a" → (0×260) + (0) = 0 (wait, this should be 260)
 *
 * @param encoded - Encoded string (even length, pairs of [0-9][a-z])
 * @returns Numeric ID as BigInt
 */
export function decodeId(encoded: string): bigint {
  if (!encoded || typeof encoded !== 'string') {
    throw new Error('Invalid encoded ID: must be a non-empty string');
  }

  // If odd length, prepend '0' to make it even
  let s = encoded;
  if (s.length % 2 === 1) {
    s = '0' + s;
  }

  // Validate format: pairs of [0-9][a-z]
  if (!/^([0-9][a-z])+$/.test(s)) {
    throw new Error(`Invalid encoded ID format: must be pairs of [0-9][a-z] (got ${encoded})`);
  }

  let result = BigInt(0);
  const CHAR_A = 97; // 'a'.charCodeAt(0)

  // Process each pair
  for (let i = 0; i < s.length; i += 2) {
    // Decode the base-260 digit from this pair
    const numberPart = parseInt(s[i], 10) * 26;
    const letterPart = s.charCodeAt(i + 1) - CHAR_A;
    const digit = numberPart + letterPart;

    // Add to result using base-260 positional notation
    result = result * BigInt(260) + BigInt(digit);
  }

  return result;
}

/**
 * Encode a database object into a global ID
 *
 * @param type - Object type (e.g., 'users', 'photos')
 * @param id - Numeric database ID
 * @returns Global ID string (e.g., "us1a", "ph2s")
 */
export function encodeGlobalId(type: ObjectType, id: number | bigint): string {
  if (!isValidObjectType(type)) {
    throw new Error(`Invalid object type: ${type}`);
  }

  const prefix = OBJECT_TYPES[type];
  const encodedId = encodeId(id);

  return `${prefix}${encodedId}`;
}

// ============================================================================
// Decoding Functions
// ============================================================================

/**
 * Result of a successful decode operation
 *
 * NOTE: Returns number instead of bigint to centralize BigInt → Number conversion.
 * This provides a single location to address BigInt serialization issues if needed.
 * Safe for PostgreSQL INTEGER/BIGINT values within JavaScript's Number.MAX_SAFE_INTEGER.
 */
export interface DecodingSuccess {
  type: ObjectType;
  id: number;
}

/**
 * Result of a failed decode operation
 */
export interface DecodingError {
  error: string;
}

/**
 * Union type for decode results
 */
export type DecodingResult = DecodingSuccess | DecodingError;

/**
 * Decode a global ID and assert it matches the expected type, throwing on any error
 *
 * This function combines validation and decoding in one step, making it convenient
 * for cases where you expect a valid global ID and want to throw immediately on failure.
 *
 * @param type - Expected object type (e.g., 'users', 'photos')
 * @param id - Global ID string to decode (e.g., "us1a", "co4m")
 * @returns Numeric database ID as Number
 * @throws Error if the type is invalid, the ID cannot be decoded, or the decoded type doesn't match
 *
 * @example
 * // Success case
 * const userId = assertDecodeGlobalId('users', 'us1a');  // Returns 26
 *
 * @example
 * // Error cases
 * assertDecodeGlobalId('users', 'invalid');  // Throws: Failed to decode ID
 * assertDecodeGlobalId('users', 'ph1a');     // Throws: Unexpected type
 */
export function assertDecodeGlobalId(type: ObjectType, id: string): number {
  if (!isValidObjectType(type)) {
    throw new Error(`Invalid object type: ${type}`);
  }
  const result = decodeGlobalId(id);
  if ('error' in result) {
    throw new Error(result.error);
  }
  if (result.type != type) {
    throw new Error('Unexpected type');
  }
  return result.id;
}

/**
 * Decode an ID that could be either a numeric ID or a global ID
 *
 * This function provides flexible ID handling for database query functions that need
 * to accept both numeric IDs (for backward compatibility or internal use) and global
 * IDs (from the API). It normalizes all inputs to a consistent DecodingResult format.
 *
 * ID Type Detection:
 * - number: Treated as numeric database ID, returned as-is
 * - bigint: Treated as numeric database ID, converted to Number
 * - string (numeric like "123"): Treated as numeric ID, converted to Number
 * - string (global ID like "co4m"): Decoded and validated
 *
 * @param type - Expected object type (e.g., 'users', 'photos')
 * @param id - ID to decode (numeric ID or global ID string)
 * @returns DecodingResult with success {type, id: number} or error {error}
 *
 * @example
 * // Numeric IDs (all return same result)
 * decodeGlobalIdOrNumber('collections', 123);     // {type: 'collections', id: 123}
 * decodeGlobalIdOrNumber('collections', 123n);    // {type: 'collections', id: 123}
 * decodeGlobalIdOrNumber('collections', "123");   // {type: 'collections', id: 123}
 *
 * @example
 * // Global ID
 * decodeGlobalIdOrNumber('collections', "co4m"); // {type: 'collections', id: 123}
 *
 * @example
 * // Type mismatch
 * decodeGlobalIdOrNumber('users', "co4m");       // {error: 'Unexpected type'}
 */
export function decodeGlobalIdOrNumber(type: ObjectType, id: bigint | number | string): DecodingResult {
  if (typeof id === 'number') {
    return { type: type, id: id };
  }
  else if (typeof id === 'bigint') {
    return { type: type, id: Number(id) };
  }
  else {
    if (/^\d+$/.test(id)) {
      return { type: type, id: Number(id) };
    }
    else {
      const result = decodeGlobalId(id);
      if ('error' in result) {
        return result;
      }
      if (result.type != type) {
        return { error: 'Unexpected type' };
      }
      return result;
    }
  }
}


/**
 * Decode a global ID back to object type and numeric ID
 *
 * @param globalId - Global ID string (e.g., "us0a", "ph0b0a")
 * @returns DecodingSuccess {type, id: number} or DecodingError {error}
 */
export function decodeGlobalId(globalId: string): DecodingResult {
  // Normalize to lowercase
  const normalized = globalId.toLowerCase();

  // Basic format validation
  if (normalized.length < 4) {
    return { error: 'Invalid global ID format: too short (minimum 4 characters)' };
  }

  if (normalized.length % 2 !== 0) {
    return { error: 'Invalid global ID format: length must be even' };
  }

  // Extract prefix
  const prefix = normalized.slice(0, 2);
  const encodedId = normalized.slice(2);

  // Validate prefix
  if (!isValidPrefix(prefix)) {
    return { error: `Unknown object type prefix: ${prefix}` };
  }

  // Decode ID
  try {
    const idBigInt = decodeId(encodedId);
    const type = PREFIX_TO_TYPE[prefix];

    // Convert BigInt to Number for consistency
    // Safe for PostgreSQL INTEGER/BIGINT within Number.MAX_SAFE_INTEGER
    return { type, id: Number(idBigInt) };
  } catch (err) {
    return { error: `Failed to decode ID: ${err instanceof Error ? err.message : 'unknown error'}` };
  }
}

/**
 * Decode an ID (numeric or global) and assert it matches the expected type
 *
 * This function is a throwing wrapper around decodeGlobalIdOrNumber(), designed
 * for database query functions that need to accept both numeric and global IDs
 * and want to fail fast on invalid input.
 *
 * This replaces the pattern of local toNumericId() helper functions duplicated
 * across query files, providing a single source of truth for ID conversion.
 *
 * @param type - Expected object type (e.g., 'users', 'photos')
 * @param id - ID to decode (numeric ID or global ID string)
 * @returns Numeric database ID as Number
 * @throws Error if type is invalid, ID cannot be decoded, or type doesn't match
 *
 * @example
 * // All return same numeric value
 * assertDecodeGlobalIdOrNumber('collections', 123);     // 123
 * assertDecodeGlobalIdOrNumber('collections', 123n);    // 123
 * assertDecodeGlobalIdOrNumber('collections', "123");   // 123
 * assertDecodeGlobalIdOrNumber('collections', "co4m");  // 123
 *
 * @example
 * // Error cases
 * assertDecodeGlobalIdOrNumber('users', 'co4m');      // Throws: Unexpected type
 * assertDecodeGlobalIdOrNumber('users', 'invalid');   // Throws: Failed to decode
 */
export function assertDecodeGlobalIdOrNumber(
  type: ObjectType,
  id: bigint | number | string
): number {
  const result = decodeGlobalIdOrNumber(type, id);

  if ('error' in result) {
    throw new Error(`Invalid ${type} ID: ${result.error}`);
  }

  return result.id;
}

// ============================================================================
// Registry Query Functions (for User Story 3)
// ============================================================================

/**
 * Get all registered object types
 *
 * @returns Array of {type, prefix} objects
 */
export function getAllObjectTypes(): Array<{ type: ObjectType; prefix: ObjectPrefix }> {
  return Object.entries(OBJECT_TYPES).map(([type, prefix]) => ({
    type: type as ObjectType,
    prefix: prefix as ObjectPrefix
  }));
}

/**
 * Calculate available (unused) two-letter prefix combinations
 *
 * Total combinations: 26 * 26 = 676
 * Used: Object.keys(OBJECT_TYPES).length
 * Available: 676 - used
 *
 * @returns Array of available prefixes
 */
export function getAvailablePrefixes(): string[] {
  const usedPrefixes = new Set<string>(Object.values(OBJECT_TYPES));
  const available: string[] = [];

  // Generate all two-letter combinations
  for (let i = 97; i <= 122; i++) { // a-z
    for (let j = 97; j <= 122; j++) {
      const prefix = String.fromCharCode(i) + String.fromCharCode(j);
      if (!usedPrefixes.has(prefix)) {
        available.push(prefix);
      }
    }
  }

  return available;
}

/**
 * Get count of available prefixes
 *
 * @returns Number of unused prefix combinations
 */
export function getAvailablePrefixCount(): number {
  const TOTAL_COMBINATIONS = 26 * 26; // 676
  const usedCount = Object.keys(OBJECT_TYPES).length;
  return TOTAL_COMBINATIONS - usedCount;
}

/**
 * Get the prefix for a given object type
 *
 * @param type - Object type
 * @returns Prefix string or undefined if type is invalid
 */
export function getPrefixForType(type: string): ObjectPrefix | undefined {
  if (isValidObjectType(type)) {
    return OBJECT_TYPES[type];
  }
  return undefined;
}

/**
 * Get the object type for a given prefix
 *
 * @param prefix - Two-character prefix
 * @returns Object type or undefined if prefix is invalid
 */
export function getTypeForPrefix(prefix: string): ObjectType | undefined {
  if (isValidPrefix(prefix)) {
    return PREFIX_TO_TYPE[prefix];
  }
  return undefined;
}
